import { useNavigate } from 'react-router';
import { DatatableCell, DatatableRow } from '@monksflow/monks-ui';
import { NotepadText } from 'lucide-react';
import {
  BriefTemplateLevelBadge,
  BriefTemplateStatusBadge,
  type BriefTemplate,
} from '@/entities/brief-template';
import { ROUTES } from '@/shared/config/routes';
import { formatTableTimestamp } from '@/shared/utils/format';
import { PreResolvedAvatarCell, TableIconBadge } from '@/shared/ui/data-table';
import { BriefTemplatesActionMenu } from '../BriefTemplatesActionMenu';

interface BriefTemplatesTableRowProps {
  template: BriefTemplate;
  /** The `brief_template_levels` flag. Off, the row renders exactly as before levels. */
  levelsEnabled?: boolean;
}

const EM_DASH = '—';

/**
 * Single-row presentation of a {@link BriefTemplate}.
 *
 * Migrated to monks-ui `Datatable*` directly (MFINS-2897 Option-A pivot):
 * - Outer `<tr>` is `<DatatableRow interactive>` — the `interactive` variant
 *   gives `cursor-pointer` + `hover:[&>*]:bg-muted` +
 *   `active:[&>*]:bg-surface-raised` at the row level; replaces the app's
 *   previous clickable-row wrapper primitive.
 * - Leading Name-cell icon framing comes from `<TableIconBadge>` (from
 *   `@/shared/ui/data-table`).
 * - Owner cell (`Created by`) uses `<PreResolvedAvatarCell>` for the
 *   pre-resolved creator; the null-creator em-dash stays wrapped in a
 *   bare `<span>` (no empty avatar circle) to keep the visual identical
 *   to pre-migration behavior — same trade-off as ProjectsTable.
 */
export function BriefTemplatesTableRow({
  template,
  levelsEnabled = false,
}: BriefTemplatesTableRowProps) {
  const navigate = useNavigate();
  const creator = template.creator ?? null;

  const handleRowClick = () => {
    void navigate(ROUTES.KNOWLEDGE_HUB_BRIEF_TEMPLATE_DETAIL(template.id));
  };

  return (
    <DatatableRow
      interactive
      data-testid="brief-template-row"
      className="h-20"
      onClick={handleRowClick}
    >
      <DatatableCell>
        <div className="flex items-center gap-3">
          <TableIconBadge>
            <NotepadText className="size-5 text-foreground" aria-hidden="true" />
          </TableIconBadge>
          <span className="text-sm font-medium text-foreground">{template.name}</span>
        </div>
      </DatatableCell>
      {levelsEnabled && (
        <DatatableCell data-testid="brief-template-level">
          <BriefTemplateLevelBadge level={template.level} />
        </DatatableCell>
      )}
      <DatatableCell data-testid="brief-template-status">
        <BriefTemplateStatusBadge
          status={template.status}
          enabled={levelsEnabled ? template.enabled : undefined}
        />
      </DatatableCell>
      <DatatableCell data-testid="brief-template-created-by">
        {creator?.name ? (
          <PreResolvedAvatarCell
            name={creator.name}
            avatarUrl={creator.avatarUrl ?? null}
            size="sm"
          />
        ) : (
          <span className="text-sm text-muted-foreground">{EM_DASH}</span>
        )}
      </DatatableCell>
      <DatatableCell data-testid="brief-template-updated-at">
        <span className="text-sm text-muted-foreground">
          {formatTableTimestamp(template.updatedAt)}
        </span>
      </DatatableCell>
      <DatatableCell className="w-14">
        <BriefTemplatesActionMenu
          templateId={template.id}
          templateName={template.name}
          level={template.level}
          enabled={template.enabled}
          levelsEnabled={levelsEnabled}
        />
      </DatatableCell>
    </DatatableRow>
  );
}
